import { Stack } from '@mui/joy';
import { useCallback, useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SubtitleSelectedBomParts } from 'bom/elements';
import { Form, FormField } from 'core/components';
import { FlexBox } from 'core/elements';
import { InputType } from 'core/enums';
import { NumberHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { PricingSourceType } from 'pricing/enums';
import { PricingHelper } from 'pricing/helpers';
import { IMargin } from 'pricing/types';
import { CostCard, PriceBasedOnBulkField, PriceCard, PriceLockTooltip } from 'quotes/elements';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import {
  ICalculatePriceForm,
  ICustomerQuote,
  ICustomerQuotePart,
  IQuotePartPriceData,
} from 'quotes/types';
import { Direction, Size, TooltipPosition } from 'theme/enums';

import { AlternatePricingSection } from './AlternatePricingSection';
import RadioChoice from './RadioChoice';
import { mapMarginsToRadioChoices } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (reset?: UseFormReset<ICalculatePriceForm>, closeSelection?: boolean) => void;
  onSubmit: (reset?: UseFormReset<ICalculatePriceForm>) => (data: ICalculatePriceForm) => void;
  selectedQuoteParts: ICustomerQuotePart[];
  margins: IMargin[];
  quote?: ICustomerQuote;
  isAlternateVersionEnabled?: boolean;
  interactive?: boolean;
}

const QuotePartUpdateMarginFormView = ({
  isOpen = false,
  onClose,
  onSubmit,
  selectedQuoteParts,
  margins,
  quote,
  isAlternateVersionEnabled,
  interactive = false,
}: IProps) => {
  const [priceData, setPriceData] = useState<IQuotePartPriceData>({});

  const { t } = useTranslation();

  const { control, watch, errors, setValue } = useFormContext();

  const isSingleSelection = selectedQuoteParts?.length === 1;
  const selectedQuotePart = isSingleSelection && selectedQuoteParts[0];
  const priceDecimals = selectedQuotePart?.quotePriceDecimals;

  const costValue = Number(watch('cost'));
  const priceBasedOnValue = watch('priceBasedOn');
  const replacementStandardCostValue = Number(watch('replacementStandardCost'));
  const activeCost =
    priceBasedOnValue === PriceBasedOn.REPLACEMENT ? replacementStandardCostValue : costValue;

  const quotePartLinesWithPriceLocked = useMemo(
    () =>
      selectedQuoteParts
        .filter((quotePart) => QuotePartHelper.isPriceLocked(quotePart))
        .map((quotePart) => [quotePart.line, quotePart.secondaryLine].filter(Boolean).join('.')),
    [selectedQuoteParts]
  );

  const showBulkPriceBasedOn = !isSingleSelection && isAlternateVersionEnabled;

  const priceLockedTooltipTitle = quotePartLinesWithPriceLocked.length
    ? t('messageEditLinesWithPriceLocked', {
        linesWithPriceLocked: quotePartLinesWithPriceLocked.join(', '),
      })
    : null;

  const isPriceLocked = useMemo(() => {
    if (!isAlternateVersionEnabled) {
      return false;
    }

    if (isSingleSelection) {
      return priceData?.priceLocked || false;
    }

    return quotePartLinesWithPriceLocked.length > 0;
  }, [
    isAlternateVersionEnabled,
    isSingleSelection,
    priceData?.priceLocked,
    quotePartLinesWithPriceLocked,
  ]);

  const onPriceChange = useCallback(
    (price: number) => {
      const pricingMarginPercent = PricingHelper.computeGpmPercentage(activeCost, price);

      setValue('margin', -1);
      setValue('customMargin', pricingMarginPercent);
      setValue('pricingMargin', pricingMarginPercent);

      if (price) {
        setPriceData((prev) => ({
          ...prev,
          priceSourceType: PricingSourceType.CUSTOM,
        }));
      }
    },
    [activeCost, setValue]
  );

  const onRadioMarginChange = useCallback(
    (value: number) => {
      const updatedMargin = -1 === value ? (watch('customMargin') || 0) / 100 : value;

      if (value !== -1) {
        setValue('customMargin', NumberHelper.round(value * 100));
      }

      const price = PricingHelper.computeAndFormatPrice(activeCost, updatedMargin, {
        decimals: priceDecimals,
      });

      setValue('price', price);
      setValue('pricingMargin', NumberHelper.round(updatedMargin * 100));

      if (price) {
        // True margin: always from quoted cost
        setValue('trueMargin', PricingHelper.computeGpmPercentage(costValue, price));
        setValue('isPricingCustom', true);

        setPriceData((prev) => ({
          ...prev,
          priceSourceType: PricingSourceType.CUSTOM,
        }));
      }
    },
    [activeCost, costValue, priceDecimals, setValue, watch]
  );

  return (
    <Form
      title={t('titleCalculatePricing')}
      subtitle={<SubtitleSelectedBomParts selectedBomParts={selectedQuoteParts} />}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelUpdatePricing')}
      onSubmit={onSubmit}
      interactive={interactive}
      drawerSlotAndSlotProps={
        isAlternateVersionEnabled
          ? { slotProps: { content: { sx: { maxWidth: '600px', width: '600px' } } } }
          : undefined
      }
    >
      {isSingleSelection && (
        <>
          {isAlternateVersionEnabled && (
            <AlternatePricingSection
              quotePart={selectedQuotePart}
              quote={quote}
              margins={margins}
              isPriceLocked={isPriceLocked}
            />
          )}

          {!isAlternateVersionEnabled && (
            <Stack direction={Direction.ROW} spacing={2}>
              <CostCard quotePart={selectedQuotePart} />
              <PriceCard
                cost={selectedQuotePart.computedCost}
                values={{
                  price: selectedQuotePart.price,
                  decimals: selectedQuotePart.quotePriceDecimals,
                  onPriceChange,
                  margin:
                    -1 === Number(watch('margin')) ? watch('customMargin') / 100 : watch('margin'),
                  control,
                  error: errors?.price,
                }}
              />
            </Stack>
          )}
        </>
      )}

      {showBulkPriceBasedOn && (
        <PriceBasedOnBulkField
          selectedQuoteParts={selectedQuoteParts}
          title={t('labelPriceBasedOn')}
        />
      )}

      <FormField
        name="margin"
        type={InputType.RADIO_GROUP}
        inputProps={{
          size: Size.MD,
          labelSize: Size.LG,
          label: (
            <FlexBox>
              {t('labelGrossProfitMargin')}
              {!isSingleSelection && quotePartLinesWithPriceLocked.length > 0 && (
                <PriceLockTooltip
                  priceLocked={isPriceLocked}
                  placement={TooltipPosition.BOTTOM_START}
                  tooltipTitle={priceLockedTooltipTitle}
                />
              )}
            </FlexBox>
          ),
          required: true,
          renderRadio: (choice) => (
            <RadioChoice
              key={choice.value}
              selectedQuotePart={selectedQuotePart}
              price={watch('price')}
              cost={activeCost}
              control={control}
              errors={errors}
              choice={choice}
              customMargin={watch('customMargin')}
              isSingleSelection={isSingleSelection}
              onChange={onRadioMarginChange}
              disabled={isPriceLocked || (selectedQuotePart && !(activeCost > 0))}
            />
          ),
          choices: mapMarginsToRadioChoices(margins),
          wrapperStyle: { gap: '14px' },
        }}
      />
    </Form>
  );
};

export default QuotePartUpdateMarginFormView;
